import React, { useEffect, useMemo, useState } from "react";
import { Drawer, Tabs, Skeleton } from "antd";
import moment from "moment";
import Button from "@dtci/spellbook/Button";
import { DetailRow } from "tap/components/DetailRow";
import { usePPTState, usePPTActions } from "tap/store/ppt/usePPT";
import {
  fetchDiscountDetails,
  xavierExperienceUrl,
  rightslineDealUrl,
  rightslineContactUrl,
} from "ppt/api";
import { PPTDiscountAuditTimeline } from "../PPTDiscountDetails/PPTDiscountAuditTimeline";
import { PPTSeverityBadge } from "../PPTSeverityBadge";
import styles from "./pptClientDiscountDrawer.module.scss";

/**
 * Drawer for a single ClientDiscount (one client × one discount realization).
 * Opened from the Client Discounts lens row click. Mirrors the discount
 * drawer's tab pattern (Details / Audit Log) but at ClientDiscount grain.
 *
 * Data path:
 *   - Details tab is fed directly from the row data passed at drawer-open
 *     time, so no spinner is needed.
 *   - Audit tab fetches the parent discount's full detail (which carries
 *     auditEvents from Pass 2) and filters to events whose payload
 *     references THIS clientDiscountId. There's no client-discount-scoped
 *     audit endpoint today; this avoids adding one.
 */

const TAB_DETAILS = "details";
const TAB_ISSUES = "issues";
const TAB_AUDIT = "audit";

const formatCurrency = (v, currency) => {
  if (v == null) return "—";
  return v.toLocaleString(undefined, {
    style: "currency",
    currency: currency || "USD",
  });
};

const formatPercent = (v) => {
  if (v == null) return "—";
  return v.toLocaleString(undefined, {
    style: "percent",
    maximumFractionDigits: 2,
  });
};

const formatDate = (v) => {
  if (v == null || v === "") return "—";
  const m = moment(v);
  return m.isValid() ? m.format("MMM D, YYYY") : String(v);
};

const Mono = ({ children }) =>
  children == null || children === "" ? (
    <span className={styles.empty}>—</span>
  ) : (
    <span className={styles.mono}>{children}</span>
  );

export const ClientDiscountDetailsTab = ({ data }) => {
  if (!data) return null;
  return (
    <>
      <section className={styles.section}>
        <h4 className={styles.sectionTitle}>Client</h4>
        <div className={styles.detailsGrid}>
          <DetailRow label="Name" value={data.client || "—"} />
          {data.clientLegalName ? (
            <DetailRow label="Legal Name" value={data.clientLegalName} />
          ) : null}
          <DetailRow
            label="Apollo ID"
            value={<Mono>{data.clientApolloId}</Mono>}
          />
          <DetailRow
            label="Rightsline Contact ID"
            value={
              data.licenseeRlContactId != null ? (
                <a
                  href={rightslineContactUrl(data.licenseeRlContactId)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Mono>{data.licenseeRlContactId}</Mono> ↗
                </a>
              ) : (
                <Mono>{data.licenseeRlContactId}</Mono>
              )
            }
            noBorder
          />
        </div>
      </section>

      <section className={styles.section}>
        <h4 className={styles.sectionTitle}>Deal</h4>
        <div className={styles.detailsGrid}>
          <DetailRow label="Rate Card" value={data.rateCardLabel || "—"} />
          <DetailRow label="Tier" value={data.tier || "—"} />
          <DetailRow
            label="Deal ID"
            value={
              data.rlDealId != null ? (
                <a
                  href={rightslineDealUrl(data.rlDealId)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Mono>{data.rlDealId}</Mono> ↗
                </a>
              ) : (
                <Mono>{data.rlDealId}</Mono>
              )
            }
          />
          <DetailRow label="Currency" value={data.currency || "—"} />
          <DetailRow
            label="Cost"
            value={formatCurrency(data.cost, data.currency)}
          />
          <DetailRow
            label="Retail"
            value={formatCurrency(data.retail, data.currency)}
          />
          <DetailRow label="Rev Share" value={formatPercent(data.revShare)} />
          <DetailRow label="Start Date" value={formatDate(data.termStart)} />
          <DetailRow label="End Date" value={formatDate(data.termEnd)} />
          <DetailRow
            label="Language"
            value={
              Array.isArray(data.language)
                ? data.language.join(", ")
                : data.language || "—"
            }
          />
          <DetailRow
            label="Transactional Window"
            value={data.transactionalWindow || "—"}
            noBorder
          />
        </div>
      </section>

      <section className={styles.section}>
        <h4 className={styles.sectionTitle}>Source Identifiers</h4>
        <div className={styles.detailsGrid}>
          <DetailRow
            label="TDRO Entity ID"
            value={<Mono>{data.tdroEntityId}</Mono>}
            noBorder={
              (data.xavierIds || []).length === 0 && data.xavierExperienceId == null
            }
          />
          {data.xavierExperienceId != null ? (
            <DetailRow
              label="Xavier Experience"
              value={
                <a
                  href={xavierExperienceUrl(data.xavierExperienceId)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Mono>{data.xavierExperienceId}</Mono> ↗
                </a>
              }
              noBorder={(data.xavierIds || []).length === 0}
            />
          ) : null}
          {(data.xavierIds || []).map((x, i, all) => (
            <DetailRow
              key={x.type}
              label={<Mono>{x.type}</Mono>}
              value={<Mono>{x.value}</Mono>}
              noBorder={i === all.length - 1}
            />
          ))}
        </div>
      </section>

      <section className={styles.section}>
        <h4 className={styles.sectionTitle}>Parent Discount</h4>
        <div className={styles.detailsGrid}>
          <DetailRow label="Title" value={data.title || "—"} />
          <DetailRow label="Studio" value={data.studio || "—"} />
          <DetailRow label="Type" value={data.contentClassification || "—"} />
          <DetailRow label="Format" value={data.format || "—"} />
          <DetailRow label="Territory" value={data.territory || "—"} />
          <DetailRow
            label="Radar ID"
            value={<Mono>{data.radarProductId}</Mono>}
            noBorder
          />
        </div>
      </section>
    </>
  );
};

const ClientDiscountIssuesTab = ({ issues, loading }) => {
  if (loading) {
    return (
      <div className={styles.section}>
        <Skeleton active paragraph={{ rows: 2 }} />
      </div>
    );
  }
  if (!issues || issues.length === 0) {
    return (
      <div className={styles.section}>
        <div className={styles.empty}>No issues for this client discount.</div>
      </div>
    );
  }
  return (
    <div className={styles.section}>
      <ul className={styles.issuesList}>
        {issues.map((issue) => (
          <li key={issue.id} className={styles.issue}>
            <PPTSeverityBadge severity={issue.severity} />
            <div className={styles.issueDescription}>{issue.description}</div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const PPTClientDiscountDrawer = () => {
  const { clientDiscountDrawerOpen, selectedClientDiscount } = usePPTState();
  const { closeClientDiscountDrawer } = usePPTActions();
  const [activeTab, setActiveTab] = useState(TAB_DETAILS);

  // The parent discount detail carries BOTH auditEvents and clientDiscountIssues,
  // so Issues and Audit derive from a single lazy fetch (not one each).
  const [parentDetail, setParentDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const childId = selectedClientDiscount?.id;
  const parentId = selectedClientDiscount?.parentDiscountId;

  // Reset to Details + drop stale detail whenever a different client discount is
  // opened, so the drawer doesn't show data from the previous selection.
  useEffect(() => {
    if (clientDiscountDrawerOpen && childId) {
      setActiveTab(TAB_DETAILS);
      setParentDetail(null);
    }
  }, [clientDiscountDrawerOpen, childId]);

  // Load the parent discount detail as soon as the drawer opens: the Issues
  // tab LABEL needs the count immediately, not on first tab activation.
  useEffect(() => {
    if (!clientDiscountDrawerOpen) return;
    if (parentDetail !== null) return; // already loaded for this selection
    if (!parentId || !childId) return;

    let cancelled = false;
    setDetailLoading(true);
    fetchDiscountDetails(parentId)
      .then((parent) => {
        if (!cancelled) setParentDetail(parent || {});
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(
          "Failed to load detail for client discount",
          childId,
          err,
        );
        setParentDetail({});
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // Deliberately excludes parentDetail/detailLoading: depending on them would
    // re-run this effect when setDetailLoading(true)/setParentDetail fire, which
    // trips the cleanup (cancelled=true) on the in-flight request and leaves the
    // spinner stuck — the original Audit Log "infinite load" bug.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientDiscountDrawerOpen, parentDetail, parentId, childId]);

  // Audit events scoped to THIS client discount.
  const auditEvents = useMemo(
    () =>
      (parentDetail?.auditEvents || []).filter(
        (e) => e?.payload?.clientDiscountId === childId,
      ),
    [parentDetail, childId],
  );

  // Issues scoped to THIS client discount (flattened from the grouped response).
  const childIssues = useMemo(
    () =>
      (parentDetail?.clientDiscountIssues?.byType || [])
        .flatMap((g) => g.issues || [])
        .filter((i) => i?.clientDiscountId === childId),
    [parentDetail, childId],
  );

  const drawerTitle =
    selectedClientDiscount?.client && selectedClientDiscount?.title
      ? `${selectedClientDiscount.client} — ${selectedClientDiscount.title}`
      : selectedClientDiscount?.client ||
        selectedClientDiscount?.title ||
        "Client Discount";

  const tabItems = useMemo(
    () => [
      {
        key: TAB_DETAILS,
        label: "Details",
        children: <ClientDiscountDetailsTab data={selectedClientDiscount} />,
      },
      {
        key: TAB_ISSUES,
        label:
          childIssues.length > 0 ? `Issues (${childIssues.length})` : "Issues",
        children: (
          <ClientDiscountIssuesTab
            issues={childIssues}
            loading={detailLoading}
          />
        ),
      },
      {
        key: TAB_AUDIT,
        label:
          auditEvents.length > 0
            ? `Audit Log (${auditEvents.length})`
            : "Audit Log",
        children: (
          <div className={styles.auditWrap}>
            <Skeleton active loading={detailLoading} paragraph={{ rows: 4 }}>
              <PPTDiscountAuditTimeline events={auditEvents} />
            </Skeleton>
          </div>
        ),
      },
    ],
    [selectedClientDiscount, childIssues, auditEvents, detailLoading],
  );

  return (
    <Drawer
      className="client-discount-drawer"
      title={drawerTitle}
      width={680}
      placement="right"
      open={clientDiscountDrawerOpen}
      onClose={closeClientDiscountDrawer}
      bodyStyle={{ padding: 0, backgroundColor: "#f5f5f5" }}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            iconType="fa-solid fa-xmark"
            labelText="close"
            styleType="link"
            onClick={closeClientDiscountDrawer}
          >
            Close
          </Button>
        </div>
      }
    >
      <Tabs
        className={styles.tabs}
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        destroyInactiveTabPane={false}
      />
    </Drawer>
  );
};
